/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { CKEDITOR5_VERSION } from '@ckeditor/ckeditor5-metadata';
import { Box, useTheme } from '@mui/material';
import StackBlitzSDK from '@stackblitz/sdk';
import { saveAs } from 'file-saver';
import { BlobWriter, TextReader, ZipWriter } from '@zip.js/zip.js';
import { type ReactNode, useContext, useMemo, useState, useEffect } from 'react';
import hash from 'object-hash';

import {
	type InstallMethod,
	type Technology,
	type CodeOutput
} from '../../../constants/installation';
import {
	GTM_EVENT_CHANGE_INTEGRATION,
	GTM_EVENT_CHANGE_TECHNOLOGY,
	GTM_EVENT_STACKBLITZ,
	GTM_EVENT_DOWNLOAD,
	GTM_EVENT_OUTPUT,
	GTM_EVENT_CONVERSION
} from '../../../constants/GTMEventNames';
import { getCapabilitiesForView } from '../../../utils/getCapabilitiesForView/getCapabilitiesForView';
import { getPluginByClassName } from '../../../utils/getPluginByClassName/getPluginByClassName';
import { getProjectFiles } from '../../../utils/getProjectFiles/getProjectFiles';
import { GTMContext } from '../../../contexts/GTMContext';
import { CustomerPortalContext } from '../../../contexts/CustomerPortalContext';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';
import { EvaluationLicenseContext } from '../../../contexts/EvaluationLicenseContext';
import { SESSION_ID } from '../../../constants/sessionId';
import { triggerStatsEvent } from '../../../utils/triggerStatsEvent/triggerStatsEvent';
import { getSnippetsSections } from '../../../utils/getSnippetsSections/getSnippetsSections';
import { EditorSettingsContext } from '../../../contexts/EditorSettingsContext';
import { getRenderKey } from '../../../utils/getRenderKey/getRenderKey';
import { getInstallationMethodToggleStatus } from '../../../utils/getInstallationMethodToggleStatus/getInstallationMethodToggleStatus';
import { getCodeOutputToggleStatus } from '../../../utils/getCodeOutputToggleStatus/getCodeOutputToggleStatus';
import type { GetProjectFilesOptions } from '../../../utils/getProjectFiles/types';

export type SnippetsProps = {
	BuilderToggleButtons: ReactNode;
};

export function Snippets( { BuilderToggleButtons }: SnippetsProps ): ReactNode {
	const {
		toolbarOptions,
		editorPlugins,
		editorType,
		editorLanguage,
		preset,
		capabilities,
		isSelectedPresetModified,
		builderUrl
	} = useContext( EditorSettingsContext );
	const {
		emitCustomerPortalEvent,
		customerPortalAccount,
		featuresAvailableInPlan,
		isLoggedIn,
		isTrialActive,
		hasCloudAccess,
		hasSelfHostedAccess,
		hasOldGPLAccess
	} = useContext( CustomerPortalContext );
	const { triggerGTMEvent, shouldEmitConversionEvent } = useContext( GTMContext );
	const evaluationLicense = useContext( EvaluationLicenseContext );

	const { palette } = useTheme();

	const [ isDownloadInProgress, setIsDownloadInProgress ] = useState( false );
	const [ isLoading, setIsLoading ] = useState( false );

	const {
		selectedTechnology, onSelectedTechnologyChange,
		selectedInstallMethod, onSelectedInstallMethodChange,
		selectedCodeOutput, onSelectedCodeOutputChange
	} = useContext( InstallationSettingsContext );

	const editorOptions = { editorPlugins, editorType, editorLanguage, toolbarOptions };

	const hasPremiumPlugins = useMemo( () => {
		return editorPlugins
			.map( getPluginByClassName )
			.some( plugin => plugin.premium );
	}, [ editorPlugins ] );

	const getProjectFilesOptions: Omit<GetProjectFilesOptions, 'target'> = {
		customerPortalAccount,
		hasPremiumPlugins,
		selectedTechnology,
		editorOptions,
		evaluationLicense,
		selectedInstallMethod,
		isLoggedIn,
		builderUrl
	};

	const capabilitiesParsed = getCapabilitiesForView( { editorType, capabilities, editorPlugins, searchQuery: '', view: 'advanced' } );
	const selectedFeatures = capabilitiesParsed
		.flatMap( capability => capability.features )
		.filter( feature => feature.isSelected || feature.isSelectedIndeterminate );
	const selectedPremiumFeatures = selectedFeatures.filter( feature => feature.isPremium );
	const allFeaturesAvailableInPlan = selectedPremiumFeatures.every( feature => featuresAvailableInPlan.includes( feature.name ) );
	const arePremiumFeaturesSelected = selectedPremiumFeatures.length > 0;

	const snippetsSections = useMemo(
		() => getSnippetsSections( {
			hasPremiumPlugins,
			editorPlugins,
			selectedTechnology,
			selectedInstallMethod,
			selectedCodeOutput,
			isLoggedIn
		} ),
		[ hasPremiumPlugins, editorPlugins, selectedTechnology, selectedInstallMethod, selectedCodeOutput, isLoggedIn ]
	);

	useEffect( () => {
		const timer = setTimeout( () => {
			setIsLoading( false );
		}, 500 );

		return () => clearTimeout( timer );
	}, [ selectedTechnology, selectedInstallMethod, selectedCodeOutput, customerPortalAccount ] );

	function handleOnTechnologyChange( newTechnology: Technology ) {
		if ( newTechnology === selectedTechnology ) {
			return;
		}

		setIsLoading( true );
		onSelectedTechnologyChange( newTechnology );

		const { disabled: shouldChangeInstallationMethod } = getInstallationMethodToggleStatus( {
			key: selectedInstallMethod,
			selectedTechnology: newTechnology,
			isLoggedIn,
			isTrialActive,
			hasCloudAccess,
			hasSelfHostedAccess,
			hasOldGPLAccess
		} );

		if ( selectedInstallMethod && shouldChangeInstallationMethod ) {
			onSelectedInstallMethodChange( null );
			onSelectedCodeOutputChange( null );
		}

		triggerGTMEvent( {
			event: GTM_EVENT_CHANGE_TECHNOLOGY,
			option: newTechnology!
		} );
	}

	function handleOnInstallMethodChange( newInstallMethod: InstallMethod ) {
		if ( newInstallMethod === selectedInstallMethod ) {
			return;
		}

		setIsLoading( true );
		onSelectedInstallMethodChange( newInstallMethod );

		const { disabled: shouldChangeCodeOutput } = getCodeOutputToggleStatus( {
			key: selectedCodeOutput,
			selectedTechnology,
			selectedInstallMethod: newInstallMethod,
			hasPremiumPlugins,
			isLoggedIn
		} );

		if ( selectedCodeOutput && shouldChangeCodeOutput ) {
			onSelectedCodeOutputChange( null );
		}

		triggerGTMEvent( {
			event: GTM_EVENT_CHANGE_INTEGRATION,
			option: newInstallMethod!
		} );
	}

	function handleOnCodeOutputChange( newCodeOutput: CodeOutput ): void {
		if ( newCodeOutput === selectedCodeOutput ) {
			return;
		}

		setIsLoading( true );
		onSelectedCodeOutputChange( newCodeOutput );

		triggerGTMEvent( {
			option: newCodeOutput as string,
			event: GTM_EVENT_OUTPUT
		} );
	}

	async function editStackBlitzHandler() {
		if ( hasPremiumPlugins ) {
			return;
		}

		handleEmitConversionEvent( 'stackblitz' );

		triggerGTMEvent( {
			event: GTM_EVENT_STACKBLITZ
		} );

		const { projectFiles, filesToOpen } = await getProjectFiles( { ...getProjectFilesOptions, target: 'stackblitz' } );

		StackBlitzSDK.openProject(
			{
				title: 'My First CKEditor 5!',
				description: 'This is your setup from CKEditor 5 Builder.',
				files: projectFiles,
				template: 'node'
			},
			{
				openFile: filesToOpen.join( ',' )
			}
		);
	}

	async function downloadHandler() {
		triggerGTMEvent( {
			event: GTM_EVENT_DOWNLOAD
		} );

		setIsDownloadInProgress( true );
		handleEmitConversionEvent( 'download' );

		const { projectFiles } = await getProjectFiles( { ...getProjectFilesOptions, target: 'download' } );
		const blobWriter = new BlobWriter();
		const zipWriter = new ZipWriter( blobWriter );

		for ( const filename in projectFiles ) {
			const fileContent = projectFiles[ filename ];
			const textReader = new TextReader( fileContent );
			await zipWriter.add( filename, textReader );
		}

		await zipWriter.close();
		const zipFileBlob = await blobWriter.getData();
		saveAs( zipFileBlob, `ckeditor5-builder-${ CKEDITOR5_VERSION }.zip` );
		setIsDownloadInProgress( false );
	}

	function handleEmitConversionEvent( action: 'copy' | 'download' | 'stackblitz' ) {
		if ( !shouldEmitConversionEvent.current ) {
			return;
		}

		shouldEmitConversionEvent.current = false;

		const isRealTimeOn = ( capabilities.find( c => c.name === 'Collaboration' ) as any ).toggleValue;

		if ( isLoggedIn ) {
			emitCustomerPortalEvent( { type: 'stage', stage: 'Builder Setup Complete' } );
		}

		triggerGTMEvent( {
			event: GTM_EVENT_CONVERSION,
			action,
			stage: selectedTechnology!,
			group: selectedInstallMethod!,
			option: preset!.name,
			isDirty: isSelectedPresetModified,
			realTime: isRealTimeOn,
			menuBar: toolbarOptions.isMenuBarUsed,
			wrapped: toolbarOptions.isCollapsable,
			additionalBlock: toolbarOptions.isBlockToolbarOn,
			additionalBalloon: toolbarOptions.isBalloonToolbarOn,
			capability: capabilitiesParsed.map( capability => {
				return {
					name: capability.name,
					selected: capability.features.filter( f => f.isSelected || f.isSelectedIndeterminate ).length,
					total: capability.features.length
				};
			} )
		} );

		triggerStatsEvent( {
			event_type: GTM_EVENT_CONVERSION,
			event_data: {
				action,
				stage: selectedTechnology!,
				group: selectedInstallMethod!,
				session_id: SESSION_ID,
				preset: preset!.name,
				is_dirty: isSelectedPresetModified,
				editor_plugins: editorPlugins,
				real_time: isRealTimeOn,
				menu_bar: toolbarOptions.isMenuBarUsed,
				wrapped: toolbarOptions.isCollapsable,
				additional_block: toolbarOptions.isBlockToolbarOn,
				additional_balloon: toolbarOptions.isBalloonToolbarOn
			},
			// @ts-expect-error ENVIRONMENT variable is injected by vite.
			environment: ENVIRONMENT
		} );
	}

	return (
		<div>
			<Box display='flex' justifyContent='space-between'>
				{ BuilderToggleButtons }
			</Box>
			<Box
				sx={ {
					border: '1px solid',
					borderColor: palette.grey[ 400 ],
					borderRadius: '4px',
					padding: '20px',
					marginTop: '10px',
					backgroundColor: 'common.white',
					position: 'relative'
				} }
			>
				{ snippetsSections.map( ( SnippetsSectionComponent, index ) => {
					const key = getRenderKey( [
						SnippetsSectionComponent.name,
						editorLanguage,
						editorPlugins,
						selectedInstallMethod,
						selectedTechnology,
						hash( customerPortalAccount ).toString(),
						index,
						builderUrl
					] );

					return (
						<SnippetsSectionComponent
							key={ key }
							number={ index }
							isLoading={ isLoading }
							downloadHandler={ downloadHandler }
							hasPremiumPlugins={ hasPremiumPlugins }
							isDownloadInProgress={ isDownloadInProgress }
							editStackBlitzHandler={ editStackBlitzHandler }
							onCodeOutputChange={ handleOnCodeOutputChange }
							onTechnologyChange={ handleOnTechnologyChange }
							onCopyEvent={ () => handleEmitConversionEvent( 'copy' ) }
							onInstallMethodChange={ handleOnInstallMethodChange }
							allFeaturesAvailableInPlan={ allFeaturesAvailableInPlan }
							arePremiumFeaturesSelected={ arePremiumFeaturesSelected }
						/>
					);
				} ) }
			</Box>
		</div>
	);
}
